import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  X,
  Box,
  FileText,
  Database,
  Settings,
  GitBranch,
  Bug,
  Sparkles,
  AlertTriangle,
  Package,
  RefreshCw,
  ClipboardCheck,
  Container,
  FileCode,
  FileArchive,
} from "lucide-react";
import {
  sectionTypesApi,
  ReleaseNoteSectionType,
  ItemSchemaField,
  ItemSchema,
} from "../../api/client";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Text Area" },
  { value: "url", label: "URL" },
  { value: "select", label: "Select (Dropdown)" },
  { value: "number", label: "Number" },
  { value: "checkbox", label: "Checkbox" },
  { value: "checklist", label: "Checklist" },
];

const CATEGORIES = [
  { value: "deployment", label: "Deployment" },
  { value: "informational", label: "Informational" },
];

const ICON_OPTIONS = [
  { value: "box", label: "Box", icon: Box },
  { value: "container", label: "Container", icon: Container },
  { value: "file-code", label: "File Code", icon: FileCode },
  { value: "file-archive", label: "File Archive", icon: FileArchive },
  { value: "database", label: "Database", icon: Database },
  { value: "file-text", label: "File Text", icon: FileText },
  { value: "settings", label: "Settings", icon: Settings },
  { value: "git-branch", label: "Git Branch", icon: GitBranch },
  { value: "git-merge", label: "Git Merge", icon: GitBranch },
  { value: "clipboard-check", label: "Clipboard Check", icon: ClipboardCheck },
  { value: "refresh-cw", label: "Refresh", icon: RefreshCw },
  { value: "sparkles", label: "Sparkles", icon: Sparkles },
  { value: "bug", label: "Bug", icon: Bug },
  { value: "alert-triangle", label: "Alert", icon: AlertTriangle },
  { value: "package", label: "Package", icon: Package },
];

const getIconComponent = (iconName: string) => {
  const iconOption = ICON_OPTIONS.find((opt) => opt.value === iconName);
  return iconOption?.icon || Box;
};

export default function SectionTypesSettings() {
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<ReleaseNoteSectionType | null>(
    null,
  );
  const [schemaFields, setSchemaFields] = useState<ItemSchemaField[]>([]);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState<string[]>([]);
  const [newOptionText, setNewOptionText] = useState("");

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["section-types"],
    queryFn: () => sectionTypesApi.list(),
  });

  const createMutation = useMutation({
    mutationKey: ["createSectionType"],
    mutationFn: sectionTypesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section-types"] });
      handleCancel();
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateSectionType"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof sectionTypesApi.update>[1]) =>
      sectionTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section-types"] });
      handleCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteSectionType"],
    mutationFn: sectionTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section-types"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const itemSchema: ItemSchema = { fields: schemaFields };

    const typeData = {
      name: formData.get("name") as string,
      slug: (formData.get("slug") as string) || undefined,
      description: formData.get("description") as string,
      category: formData.get("category") as "deployment" | "informational",
      automation_key: formData.get("automation_key") as string,
      jenkins_job_pattern: formData.get("jenkins_job_pattern") as string,
      icon: formData.get("icon") as string,
      item_schema: itemSchema,
      display_order:
        editingType?.display_order ?? data?.section_types?.length ?? 0,
      is_active: editingType?.is_active ?? true,
    };

    if (editingType) {
      updateMutation.mutate({ id: editingType.id, ...typeData });
    } else {
      createMutation.mutate(typeData);
    }
  };

  const handleEdit = (sectionType: ReleaseNoteSectionType) => {
    setEditingType(sectionType);
    setSchemaFields(sectionType.item_schema?.fields || []);
    setShowForm(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (
      confirm(`Are you sure you want to delete the "${name}" section type?`)
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingType(null);
    setSchemaFields([]);
    resetNewField();
  };

  const resetNewField = () => {
    setNewFieldName("");
    setNewFieldLabel("");
    setNewFieldType("text");
    setNewFieldRequired(false);
    setNewFieldOptions([]);
    setNewOptionText("");
  };

  const handleAddField = () => {
    if (!newFieldName.trim() || !newFieldLabel.trim()) return;

    const newField: ItemSchemaField = {
      name: newFieldName.trim().toLowerCase().replace(/\s+/g, "_"),
      label: newFieldLabel.trim(),
      type: newFieldType as ItemSchemaField["type"],
      required: newFieldRequired,
      options: newFieldType === "select" ? newFieldOptions : undefined,
    };

    setSchemaFields([...schemaFields, newField]);
    resetNewField();
  };

  const handleRemoveField = (index: number) => {
    setSchemaFields(schemaFields.filter((_, i) => i !== index));
  };

  const handleAddOption = () => {
    if (
      newOptionText.trim() &&
      !newFieldOptions.includes(newOptionText.trim())
    ) {
      setNewFieldOptions([...newFieldOptions, newOptionText.trim()]);
      setNewOptionText("");
    }
  };

  const handleRemoveOption = (option: string) => {
    setNewFieldOptions(newFieldOptions.filter((o) => o !== option));
  };

  const toggleActive = async (sectionType: ReleaseNoteSectionType) => {
    await updateMutation.mutateAsync({
      id: sectionType.id,
      is_active: !sectionType.is_active,
    });
  };

  // Group section types by category
  const deploymentTypes =
    data?.section_types?.filter((t) => t.category === "deployment") || [];
  const informationalTypes =
    data?.section_types?.filter((t) => t.category === "informational") || [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Section Types
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure section types for release notes (e.g., Container Deploy,
            Config Changes, Features)
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingType(null);
            setSchemaFields([]);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Section Type
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingType ? "Edit Section Type" : "Add Section Type"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="input"
                  placeholder="Container Deploy"
                  defaultValue={editingType?.name}
                  required
                />
              </div>
              <div>
                <label htmlFor="slug" className="label">
                  Slug (auto-generated if empty)
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  className="input"
                  placeholder="container_deploy"
                  defaultValue={editingType?.slug}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="category" className="label">
                  Category
                </label>
                <select
                  name="category"
                  id="category"
                  className="input"
                  defaultValue={editingType?.category || "deployment"}
                  required
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="icon" className="label">
                  Icon
                </label>
                <select
                  name="icon"
                  id="icon"
                  className="input"
                  defaultValue={editingType?.icon || "box"}
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <input
                type="text"
                name="description"
                id="description"
                className="input"
                placeholder="Docker container deployments"
                defaultValue={editingType?.description}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="automation_key" className="label">
                  Automation Key (for Jenkins API)
                </label>
                <input
                  type="text"
                  name="automation_key"
                  id="automation_key"
                  className="input"
                  placeholder="containers"
                  defaultValue={editingType?.automation_key}
                />
              </div>
              <div>
                <label htmlFor="jenkins_job_pattern" className="label">
                  Jenkins Job Pattern (optional)
                </label>
                <input
                  type="text"
                  name="jenkins_job_pattern"
                  id="jenkins_job_pattern"
                  className="input"
                  placeholder="deploy-*"
                  defaultValue={editingType?.jenkins_job_pattern}
                />
              </div>
            </div>

            {/* Item Schema Fields */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Item Fields
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Define the fields that items in this section type will have.
              </p>

              {/* Existing fields */}
              {schemaFields.length > 0 && (
                <div className="mb-4 space-y-2">
                  {schemaFields.map((field, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <GripVertical className="h-4 w-4 text-gray-400" />
                        <div>
                          <span className="font-medium text-sm">
                            {field.label}
                          </span>
                          <span className="text-gray-500 text-xs ml-2">
                            ({field.name})
                          </span>
                        </div>
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600">
                          {FIELD_TYPES.find((t) => t.value === field.type)
                            ?.label || field.type}
                        </span>
                        {field.required && (
                          <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-600">
                            Required
                          </span>
                        )}
                        {field.options && (
                          <span className="text-xs text-gray-500">
                            {field.options.length} options
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveField(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new field */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600">Field Name</label>
                    <input
                      type="text"
                      value={newFieldLabel}
                      onChange={(e) => {
                        setNewFieldLabel(e.target.value);
                        setNewFieldName(
                          e.target.value.toLowerCase().replace(/\s+/g, "_"),
                        );
                      }}
                      className="input text-sm"
                      placeholder="Component Name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Slug</label>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="input text-sm"
                      placeholder="component_name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Type</label>
                    <select
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value)}
                      className="input text-sm"
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newFieldRequired}
                        onChange={(e) => setNewFieldRequired(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="btn btn-secondary text-sm"
                      disabled={!newFieldLabel.trim()}
                    >
                      Add Field
                    </button>
                  </div>
                </div>

                {/* Options for select type */}
                {newFieldType === "select" && (
                  <div className="mt-3 pt-3 border-t">
                    <label className="text-xs text-gray-600">Options</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="text"
                        value={newOptionText}
                        onChange={(e) => setNewOptionText(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), handleAddOption())
                        }
                        className="input text-sm flex-1"
                        placeholder="Add option..."
                      />
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="btn btn-secondary text-sm"
                      >
                        Add
                      </button>
                    </div>
                    {newFieldOptions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {newFieldOptions.map((option, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-200"
                          >
                            {option}
                            <button
                              type="button"
                              onClick={() => handleRemoveOption(option)}
                              className="ml-1 text-gray-500 hover:text-gray-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingType ? "Update Section Type" : "Create Section Type"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Section Types List */}
      <div className="mt-6 space-y-6">
        {isLoading ? (
          <div className="card">
            <p className="text-gray-500">Loading section types...</p>
          </div>
        ) : !data?.section_types?.length ? (
          <div className="card">
            <p className="text-gray-500 text-center py-8">
              No section types configured. Section types define what can be
              added to deployment release notes.
            </p>
          </div>
        ) : (
          <>
            {/* Deployment Section Types */}
            <div className="card overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b">
                <h3 className="text-sm font-medium text-gray-900">
                  Deployment Sections
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Sections for deployment artifacts and configuration changes
                </p>
              </div>
              {deploymentTypes.length === 0 ? (
                <p className="px-6 py-4 text-sm text-gray-500">
                  No deployment section types
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Section Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fields
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Automation Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deploymentTypes.map((sectionType) => {
                      const IconComponent = getIconComponent(
                        sectionType.icon || "box",
                      );
                      return (
                        <tr
                          key={sectionType.id}
                          className={!sectionType.is_active ? "bg-gray-50" : ""}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <GripVertical className="h-4 w-4 text-gray-400 mr-2 cursor-grab" />
                              <IconComponent className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {sectionType.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {sectionType.slug}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {sectionType.item_schema?.fields?.length || 0}{" "}
                              fields
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {sectionType.automation_key && (
                              <code className="px-2 py-1 text-xs bg-gray-100 rounded">
                                {sectionType.automation_key}
                              </code>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleActive(sectionType)}
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                sectionType.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {sectionType.is_active ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleEdit(sectionType)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(sectionType.id, sectionType.name)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Informational Section Types */}
            <div className="card overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b">
                <h3 className="text-sm font-medium text-gray-900">
                  Informational Sections
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Sections for features, bugs, known issues, and other notes
                </p>
              </div>
              {informationalTypes.length === 0 ? (
                <p className="px-6 py-4 text-sm text-gray-500">
                  No informational section types
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Section Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fields
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Automation Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {informationalTypes.map((sectionType) => {
                      const IconComponent = getIconComponent(
                        sectionType.icon || "box",
                      );
                      return (
                        <tr
                          key={sectionType.id}
                          className={!sectionType.is_active ? "bg-gray-50" : ""}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <GripVertical className="h-4 w-4 text-gray-400 mr-2 cursor-grab" />
                              <IconComponent className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {sectionType.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {sectionType.slug}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">
                              {sectionType.item_schema?.fields?.length || 0}{" "}
                              fields
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {sectionType.automation_key && (
                              <code className="px-2 py-1 text-xs bg-gray-100 rounded">
                                {sectionType.automation_key}
                              </code>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleActive(sectionType)}
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                sectionType.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {sectionType.is_active ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleEdit(sectionType)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(sectionType.id, sectionType.name)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
